import React, { useState } from 'react';

const NZIABox = ({ selectedCase, caseData }) => {
  const [activeTab, setActiveTab] = useState('auctions');

  // Determine NZIA compliance based on case
  const getNZIACompliance = (caseKey) => {
    if (!caseKey) return null;

    // Based on slide 18: Cases 2-5 meet resilience criteria (not China)
    // Case 1 (100% CN) does not meet criteria
    const caseNumber = parseInt(caseKey.replace('Case ', ''));

    // Cases 2-4 involve Chinese-owned companies operating outside China
    const hasChineseOwnership = caseNumber >= 2 && caseNumber <= 4;

    return {
      publicProcurement: {
        resilience: caseNumber > 1, // Mandatory when 50% from single country
        environmental: 'criteria-dependent', // Requires demonstration of specific sustainability metrics
      },
      auctions: {
        resilience: caseNumber > 1, // Key criteria: inverters, PV modules, solar cells + 1 component not from China
        environmental: 'criteria-dependent', // Requires demonstration of specific sustainability metrics
        resilienceWeight: '15-30%', // Weight in final score
        italianException: hasChineseOwnership, // Italian interpretation: prequalification criterion in tenders
      },
      otherSchemes: {
        sustainability: 'criteria-dependent',
        resilience: caseNumber > 1,
        bonus: caseNumber > 1, // Provides additional financial compensation
      },
      caseNumber: caseNumber,
    };
  };

  const compliance = getNZIACompliance(selectedCase);

  if (!selectedCase || !caseData) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-2 h-8 rounded-full" style={{ backgroundImage: 'linear-gradient(to bottom, #E78E42, #EAC144)' }}></div>
          <h3 className="text-2xl font-bold" style={{ color: '#000755' }}>NZIA Compliance</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-12">
          <svg className="w-20 h-20 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <p className="text-gray-500 text-center">Select a case to view NZIA compliance assessment</p>
        </div>
      </div>
    );
  }

  const renderComplianceIndicator = (status) => {
    if (status === 'criteria-dependent') {
      return (
        <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          Criteria-Dependent
        </span>
      );
    }

    return status ? (
      <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        Compliant
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        Non-Compliant
      </span>
    );
  };

  const renderPublicProcurement = () => (
    <div className="space-y-4">
      <div className="p-4 rounded-lg border-2" style={{ borderColor: '#E78E42', backgroundColor: 'rgba(231, 142, 66, 0.05)' }}>
        <div className="flex items-center justify-between mb-2">
          <h5 className="font-bold" style={{ color: '#000755' }}>Mandatory Criteria</h5>
        </div>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <strong className="text-gray-900">Resilience</strong>
                {renderComplianceIndicator(compliance.publicProcurement.resilience)}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Applied when 50% of supply chain is geographically concentrated in a single country or approaching
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <strong className="text-gray-900">Environmental Sustainability</strong>
                {renderComplianceIndicator(compliance.publicProcurement.environmental)}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Pre-qualification or award criterion - Requires demonstration of specific sustainability metrics
              </p>
              <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Compliance depends on demonstrating specific environmental criteria such as carbon footprint, renewable energy usage in manufacturing, circular economy practices, and other sustainability metrics defined by procurement requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-500 italic p-3 bg-gray-50 rounded-lg">
        <strong>Scope:</strong> Medium & large rooftops (C&I) - Chapter 4 Article 25 https://eur-lex.europa.eu/eli/reg/2024/1735/oj
      </div>
    </div>
  );

  const renderAuctions = () => (
    <div className="space-y-4">
      <div className="p-4 rounded-lg border-2" style={{ borderColor: '#0A3C80', backgroundColor: 'rgba(10, 60, 128, 0.05)' }}>
        <div className="flex items-center justify-between mb-2">
          <h5 className="font-bold" style={{ color: '#000755' }}>Key Criteria (15-30% weight)</h5>
        </div>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#0A3C80' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <strong className="text-gray-900">Resilience</strong>
                {renderComplianceIndicator(compliance.auctions.resilience)}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Applied when 50% of supply chain is geographically concentrated in a single third country or approaching
              </p>
              {compliance.auctions.resilience && (
                <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'rgba(231, 142, 66, 0.1)' }}>
                  <p className="text-xs font-semibold" style={{ color: '#E78E42' }}>
                    ✓ Inverters, PV modules, solar cells + one more specific component (e.g., glass, trackers) cannot originate from China
                  </p>
                </div>
              )}
              {compliance.auctions.italianException && (
                <div className="mt-2 p-3 rounded-lg bg-amber-50 border-2 border-amber-400">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-amber-900 mb-1">⚠️ Italian Auction Prequalification - Ownership Alert</p>
                      <p className="text-xs text-amber-900">
                        Under NZIA specific Italian auction regulations, <strong>if manufacturing is performed by Chinese-owned companies operating outside China, modules are excluded as a prequalification criterion</strong>, even if production occurs in third countries. Please verify company ownership when participating in Italian tender/auction procedures.
                      </p>
                      <p className="text-xs text-amber-800 mt-2 italic">
                        Reference: <em>Decreto del Ministro dell'ambiente e della sicurezza energetica 4 agosto 2025 recante "Modifiche al decreto del Ministro dell'ambiente e della sicurezza energetica 30 dicembre 2024, c.d. FERX Transitorio"</em>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#0A3C80' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <strong className="text-gray-900">Environmental Sustainability</strong>
                {renderComplianceIndicator(compliance.auctions.environmental)}
              </div>
              <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Compliance depends on demonstrating specific environmental criteria such as carbon footprint, renewable energy usage in manufacturing, circular economy practices, and other sustainability metrics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-500 italic p-3 bg-gray-50 rounded-lg">
        <strong>Scope:</strong> Medium & large PV systems (mostly utility-scale) - Chapter 4 Article 26 https://eur-lex.europa.eu/eli/reg/2024/1735/oj
      </div>
    </div>
  );

  const renderOtherSchemes = () => (
    <div className="space-y-4">
      <div className="p-4 rounded-lg border-2" style={{ borderColor: '#EAC144', backgroundColor: 'rgba(234, 193, 68, 0.05)' }}>
        <div className="flex items-center justify-between mb-2">
          <h5 className="font-bold" style={{ color: '#000755' }}>Mandatory Criteria</h5>
        </div>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#EAC144' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <strong className="text-gray-900">Resilience Criterion</strong>
                {renderComplianceIndicator(compliance.otherSchemes.resilience)}
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Supply chain diversification requirement
              </p>
              {compliance.otherSchemes.bonus && (
                <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'rgba(234, 193, 68, 0.15)' }}>
                  <p className="text-xs font-semibold" style={{ color: '#EAC144' }}>
                    ✓ Eligible for bonus/additional financial compensation
                  </p>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <strong className="text-gray-900">Sustainability Criterion</strong>
                  {renderComplianceIndicator(compliance.otherSchemes.sustainability)}
                </div>
                <div className="p-2 rounded bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-800">
                    <strong>Note:</strong> Environmental sustainability compliance depends on demonstrating specific criteria such as carbon footprint, renewable energy usage, and circular economy practices.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-500 italic p-3 bg-gray-50 rounded-lg">
        <strong>Scope:</strong> Small & medium rooftops (mostly residential) - Chapter 4 Article 28 https://eur-lex.europa.eu/eli/reg/2024/1735/oj
      </div>
    </div>
  );

  return (
    <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow duration-300">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-2 h-8 rounded-full" style={{ backgroundImage: 'linear-gradient(to bottom, #E78E42, #EAC144)' }}></div>
        <h3 className="text-2xl font-bold" style={{ color: '#000755' }}>NZIA Compliance</h3>
      </div>

      <div className="mb-4 p-4 rounded-lg border-2" style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.1), rgba(234, 193, 68, 0.1))', borderColor: '#E78E42' }}>
        <p className="text-sm text-gray-700">
          <strong style={{ color: '#000755' }}>Net Zero Industry Act (NZIA)</strong> aims to ensure that the clean-energy transition is not put at risk by strategic dependencies
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b-2" style={{ borderColor: '#E0E0E0' }}>
        <button
          onClick={() => setActiveTab('auctions')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'auctions' ? 'border-b-2' : ''
          }`}
          style={{
            color: activeTab === 'auctions' ? '#0A3C80' : '#6B7280',
            borderColor: activeTab === 'auctions' ? '#0A3C80' : 'transparent'
          }}
        >
          Auctions
        </button>
        <button
          onClick={() => setActiveTab('procurement')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'procurement' ? 'border-b-2' : ''
          }`}
          style={{
            color: activeTab === 'procurement' ? '#E78E42' : '#6B7280',
            borderColor: activeTab === 'procurement' ? '#E78E42' : 'transparent'
          }}
        >
          Public Procurement
        </button>
        <button
          onClick={() => setActiveTab('schemes')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'schemes' ? 'border-b-2' : ''
          }`}
          style={{
            color: activeTab === 'schemes' ? '#EAC144' : '#6B7280',
            borderColor: activeTab === 'schemes' ? '#EAC144' : 'transparent'
          }}
        >
          Other Schemes
        </button>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'auctions' && renderAuctions()}
        {activeTab === 'procurement' && renderPublicProcurement()}
        {activeTab === 'schemes' && renderOtherSchemes()}
      </div>

      {/* Overall Assessment */}
      <div className="mt-6 p-4 rounded-xl border-2" style={{
        backgroundImage: compliance.auctions.resilience
          ? 'linear-gradient(to right, rgba(34, 197, 94, 0.1), rgba(22, 163, 74, 0.1))'
          : 'linear-gradient(to right, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.1))',
        borderColor: compliance.auctions.resilience ? '#22C55E' : '#EF4444'
      }}>
        <div className="flex items-center gap-2 mb-2">
          {compliance.auctions.resilience ? (
            <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
          <strong className="text-lg" style={{ color: '#000755' }}>Overall NZIA Assessment</strong>
        </div>
        <p className="text-sm text-gray-700 mb-3">
          {compliance.auctions.resilience ? (
            <>
              This case meets NZIA resilience criteria by ensuring supply chain diversification away from single-country dependencies. Components qualify for resilience-based benefits in procurement processes.
              {compliance.caseNumber >= 2 && compliance.caseNumber <= 4 && (
                <strong className="block mt-2 text-amber-800">
                  ⚠️ Important: For NZIA specific Italian auction tenders, verify company ownership. If manufacturing is performed by Chinese-owned companies (regardless of production location), modules may be excluded as a prequalification criterion under Italian regulations.
                </strong>
              )}
            </>
          ) : (
            <>This case does not meet NZIA resilience criteria as it relies heavily on a single country for supply chain components, creating strategic dependencies that NZIA aims to mitigate.</>
          )}
        </p>

        {/* Key Implications */}
        <div className="mt-4 pt-3 border-t" style={{ borderColor: compliance.auctions.resilience ? '#22C55E' : '#EF4444' }}>
          <strong className="text-sm block mb-2" style={{ color: '#000755' }}>Key Implications:</strong>
          <ul className="text-sm text-gray-700 space-y-1.5">
            {compliance.auctions.resilience ? (
              <>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Eligible for resilience criteria weighting (15-30%) in auction schemes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Meets mandatory requirements for public procurement of rooftop installations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Qualifies for additional financial compensation in residential support schemes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Contributes to EU strategic autonomy and energy security goals</span>
                </li>
                {compliance.caseNumber >= 2 && compliance.caseNumber <= 4 && (
                  <li className="flex items-start gap-2 mt-3 p-2 bg-amber-50 rounded border border-amber-300">
                    <span className="text-amber-600 mt-0.5">⚠️</span>
                    <span className="text-amber-900"><strong>Italian Auction Alert:</strong> May be excluded from Italian NZIA specific tenders if manufacturing involves Chinese-owned companies - verify ownership (Decreto MASE 4 agosto 2025)</span>
                  </li>
                )}
              </>
            ) : (
              <>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-0.5">✗</span>
                  <span>Does not qualify for resilience criteria scoring in auctions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-0.5">✗</span>
                  <span>May face restrictions in public procurement tenders</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-0.5">✗</span>
                  <span>Not eligible for resilience-based financial bonuses</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-0.5">✗</span>
                  <span>Presents strategic dependency risks for EU energy transition</span>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>

      {/* Learn More About NZIA Compliance */}
      <div className="mt-6 rounded-xl p-6 shadow-lg" style={{ backgroundImage: 'linear-gradient(to right, #0A3C80, #000755)' }}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="flex-shrink-0 bg-white rounded-lg p-4 shadow-lg">
            <img
              src="/new_logo.svg"
              alt="SolarIntelligence.ai"
              className="h-16 sm:h-20 w-auto"
            />
          </div>
          <div className="flex-1">
            <h4 className="text-xl font-bold mb-2 text-white">
              Learn More About NZIA Compliance
            </h4>
            <p className="text-sm text-blue-50 mb-4">
              <strong className="text-white">SolarIntelligence.ai</strong> provides advanced analytics for solar intelligence.
              Our dedicated NZIA agent helps you understand the Net-Zero Industry Act and assess compliance requirements for your manufacturing scenarios.
            </p>
            <a
              href="https://solarintelligence.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-xl transition-all text-sm"
              style={{ backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)', color: 'white' }}
            >
              <span>Visit SolarIntelligence.ai</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NZIABox;
